import { useEffect, useRef } from 'react';
import { getCardPrimaryActionAndHasBack } from './getCardPrimaryActionAndHasBack';

/**
 * `swallow` is a key the tour owns but has nothing to do with: it is consumed
 * so it cannot also reach the page's own shortcut for it.
 */
export type TourKeyAction = 'next' | 'back' | 'done' | 'dismiss' | 'swallow';

type TourKeyContext = {
  key: string;
  isRepeat: boolean;
  hasModifier: boolean;
  isStepShowing: boolean;
  stepIndex: number;
  totalSteps: number;
  isForwardStepOnly: boolean;
};

export const resolveTourKeyAction = ({
  key,
  isRepeat,
  hasModifier,
  isStepShowing,
  stepIndex,
  totalSteps,
  isForwardStepOnly,
}: TourKeyContext): TourKeyAction | null => {
  if (key === 'Escape') return isRepeat ? null : 'dismiss';
  if (key !== 'ArrowLeft' && key !== 'ArrowRight') return null;
  if (hasModifier || !isStepShowing) return null;
  const { hasBack, primaryAction } = getCardPrimaryActionAndHasBack({
    stepIndex,
    totalSteps,
    isForwardStepOnly,
  });
  if (key === 'ArrowLeft' && !hasBack) return null;
  if (isRepeat) return 'swallow';
  return key === 'ArrowRight' ? primaryAction : 'back';
};

type TourKeyboardNavigationArgs = {
  isStepShowing: boolean;
  stepIndex: number;
  totalSteps: number;
  isForwardStepOnly: boolean;
  onNext: VoidFunction;
  onPrev: VoidFunction;
  onComplete: VoidFunction;
  onDismiss: VoidFunction;
};

export const useTourKeyboardNavigation = (
  args: TourKeyboardNavigationArgs
): void => {
  const argsRef = useRef(args);

  useEffect(() => {
    argsRef.current = args;
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const {
        isStepShowing,
        stepIndex,
        totalSteps,
        isForwardStepOnly,
        onNext,
        onPrev,
        onComplete,
        onDismiss,
      } = argsRef.current;
      const action = resolveTourKeyAction({
        key: event.key,
        isRepeat: event.repeat,
        hasModifier:
          event.altKey || event.ctrlKey || event.metaKey || event.shiftKey,
        isStepShowing,
        stepIndex,
        totalSteps,
        isForwardStepOnly,
      });
      if (!action) return;
      if (action !== 'dismiss') {
        event.preventDefault();
        event.stopPropagation();
      }
      if (action === 'swallow') return;
      const handlers: Record<
        Exclude<TourKeyAction, 'swallow'>,
        VoidFunction
      > = {
        next: onNext,
        back: onPrev,
        done: onComplete,
        dismiss: onDismiss,
      };
      handlers[action]();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);
};
